import React, { Children, forwardRef } from "react"
import styled from "@emotion/styled"
import { variant } from "styled-system"
import Box, { BoxProps } from "./Box"

const buttonFontBase = {
  fontFamily: "heading",
  fontSize: [18],
  lineHeight: "110%",
  letterSpacing: ["0.02em", "0.015em", "0.015em"],
  fontWeight: ["600"],
}

const secondaryFontBase = {
  fontFamily: "heading",
  fontSize: [16],
  lineHeight: "110%",
  fontWeight: ["600"],
}

// const leadingTrimStyle = {
//   mt: buttonFontBase.fontSize.map(fontSize => `-${fontSize * 0.25}px`),
//   transform: buttonFontBase.fontSize.map(
//     fontSize => `translateY(${fontSize * 0.25}px)`
//   ),
// }

const primaryButtonBase = {
  ...buttonFontBase,
  whiteSpace: "nowrap",
  borderWidth: 1.5,
  borderStyle: "solid",
  transition: "all 0.2s",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "40px",
  display: "inline-flex",
}

const secondaryButtonBase = {
  ...secondaryFontBase,
  whiteSpace: "nowrap",
  px: 3,
  height: 44,
  borderWidth: [1],
  borderStyle: "solid",
  transition: "all 0.2s",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "40px",
  display: "inline-flex",
}

const thirdButtonBase = {
  ...secondaryFontBase,
  whiteSpace: "normal",
  px: 5,
  height: 38,
  borderWidth: [1],
  borderStyle: "solid",
  transition: "all 0.2s",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "40px",
  display: "inline-flex",
}

const forthButtonBase = {
  ...secondaryFontBase,
  whiteSpace: "normal",
  px: 5,
  height: 44,
  transition: "all 0.2s",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "40px",
  display: "inline-flex",
}

const Button = styled(Box)<BoxProps>`
  border: none;
  cursor: pointer;
  outline: none;
  min-width: ${props => props.minWidth || "unset"};
  ${variant({
    variants: {
      primarySolidBlue500: {
        ...primaryButtonBase,
        padding: "10px 18px 10px 22px",
        height: 44,
        bg: "#0577DA",
        color: "#fff",
        borderColor: "transparent",
      },
      primaryBlueLightBg: {
        ...primaryButtonBase,
        px: 4,
        height: 60,
        bg: "#0058AA",
        color: "#fff",
        borderColor: "transparent",
      },
      primaryBlue: {
        ...primaryButtonBase,
        px: 4,
        height: 60,
        bg: "#1199FA",
        color: "#fff",
        borderColor: "transparent",
      },
      primaryBlack: {
        ...primaryButtonBase,
        px: 3,
        py: "9px",
        height: 44,
        bg: "#0B1426",
        color: "#fff",
        borderColor: "transparent",
      },
      primaryDark: {
        ...primaryButtonBase,
        px: 3,
        height: 44,
        bg: "primaryPaleWhite",
        color: "secondaryDarkNavy",
        borderColor: "transparent",
        "&:hover": {
          borderColor: "primaryBrightBlue",
          bg: "transparent",
          color: "primaryPaleWhite",
        },
      },
      primaryDarkNarrow: {
        ...forthButtonBase,
        px: 5,
        height: 44,
        bg: "primaryPaleWhite",
        color: "secondaryDarkNavy",
        borderColor: "transparent",
      },
      primaryLight: {
        ...primaryButtonBase,
        px: 4,
        height: 54,
        bg: "secondaryDarkNavy",
        color: "primaryPaleWhite",
        borderColor: "transparent",
        "&:hover": {
          borderColor: "secondaryDarkNavy",
          bg: "transparent",
          color: "secondaryDarkNavy",
        },
      },
      primarySmallDark: {
        ...primaryButtonBase,
        px: 3,
        height: 52,
        bg: "primaryPaleWhite",
        color: "secondaryDarkNavy",
        borderColor: "transparent",
        "&:hover": {
          borderColor: "primaryBrightBlue",
          bg: "transparent",
          color: "primaryPaleWhite",
        },
      },
      primarySmallLight: {
        ...primaryButtonBase,
        px: 3,
        height: 52,
        bg: "secondaryDarkNavy",
        color: "primaryPaleWhite",
        borderColor: "transparent",
        "&:hover": {
          borderColor: "secondaryDarkNavy",
          bg: "transparent",
          color: "secondaryDarkNavy",
        },
      },
      primaryExtraSmallDark: {
        ...primaryButtonBase,
        px: [1.5, 3, 3],
        height: [40, 42, 48],
        bg: "primaryPaleWhite",
        color: "secondaryDarkNavy",
        borderColor: "transparent",
        "&:hover": {
          borderColor: "primaryBrightBlue",
          bg: "transparent",
          color: "primaryPaleWhite",
        },
      },
      primaryExtraSmallLight: {
        ...primaryButtonBase,
        px: [1.5, 3, 3],
        height: [40, 42, 48],
        bg: "secondaryDarkNavy",
        color: "primaryPaleWhite",
        borderColor: "transparent",
        "&:hover": {
          borderColor: "secondaryDarkNavy",
          bg: "transparent",
          color: "secondaryDarkNavy",
        },
      },
      secondaryDark: {
        ...secondaryButtonBase,
        borderColor: "primaryPaleWhite",
        bg: "transparent",
        color: "white",
        "&:hover": {
          bg: "secondaryDarkButtonHoverBg",
        },
      },
      secondaryDarkBlurBg: {
        ...secondaryButtonBase,
        borderColor: "primaryPaleWhite",
        bg: "rgba(1, 1, 24, 0.50)",
        backdropFilter: "blur(6px)",
        color: "white",
        "&:hover": {
          bg: "secondaryDarkButtonHoverBg",
        },
      },
      secondaryDarkSquare: {
        ...secondaryButtonBase,
        borderRadius: "4px !important",
        borderWidth: "0.5px !important",
        borderColor: "#F4F4F4",
        bg: "transparent",
        color: "white",
        "&:hover": {
          bg: "secondaryDarkButtonHoverBg",
        },
      },
      secondaryDarkSmall: {
        ...thirdButtonBase,
        borderColor: "primaryPaleWhite",
        bg: "transparent",
        color: "white",
        "&:hover": {
          bg: "secondaryDarkButtonHoverBg",
        },
      },
      secondaryDarkSmallFont: {
        ...secondaryButtonBase,
        whiteSpace: "normal",
        fontSize: [16, 18, 18],
        px: ["20px", 3, 3],
        borderColor: "primaryPaleWhite",
        bg: "transparent",
        color: "white",
        "&:hover": {
          bg: "secondaryDarkButtonHoverBg",
        },
      },
      exchangeButton: {
        ...secondaryButtonBase,
        bg: "transparent",
        borderColor: "secondaryDarkNavy",
        color: "secondaryDarkNavy",
      },
      secondaryLight: {
        ...secondaryButtonBase,
        borderColor: "#00254F",
        bg: "transparent",
        color: "#00254F",
        "&:hover": {
          bg: "secondaryLightButtonHoverBg",
        },
      },
      secondaryBlue: {
        ...secondaryButtonBase,
        borderColor: "secondaryPartnerBlue",
        bg: "transparent",
        color: "secondaryPartnerBlue",
        "&:hover": {
          bg: "secondaryLightButtonHoverBg",
        },
      },
      plain: {
        bg: "transparent",
        p: 0,
        textAlign: "left",
      },
      navMenu: {
        bg: "transparent",
        px: [4, 8],
        textAlign: "left",
      },
      porSecondaryLight: {
        ...secondaryButtonBase,
        px: ["105px", "15px", "40px"],
        height: ["52px", "44px"],
        borderColor: "secondaryDarkNavy",
        bg: "transparent",
        color: "secondaryDarkNavy",
        "&:hover": {
          bg: "secondaryLightButtonHoverBg",
        },
      },
      porPrimaryLight: {
        ...primaryButtonBase,
        px: ["0px", "40px", "40px"],
        width: ["100%", "unset", "unset"],
        height: "54px",
        bg: "rgba(0, 37, 79, 1)",
        color: "white",
        borderColor: "transparent",
        "&:hover": {
          borderColor: "secondaryDarkNavy",
          bg: "transparent",
          color: "secondaryDarkNavy",
        },
      },
      primaryLightWithoutHover: {
        ...primaryButtonBase,
        px: ["0px", "40px", "40px"],
        width: ["100%", "unset", "unset"],
        height: "54px",
        bg: "rgba(0, 37, 79, 1)",
        color: "white",
        borderColor: "transparent",
      },
      secondarySolidBlue800: {
        ...secondaryButtonBase,
        px: "18px",
        bg: "#00254F",
        color: "#FFF",
        borderColor: "transparent",
      },
    },
  })}
`

interface ButtonProps extends BoxProps {
  variant?: string
}

const ForwardedButton = forwardRef<HTMLAllCollection, ButtonProps>(
  ({ children, ...props }, ref) => {
    return (
      <Button as="button" ref={ref} {...props}>
        {Children.map(children, child => {
          if (typeof child === "string") {
            return <Box as="span">{child}</Box>
          }

          return child
        })}
      </Button>
    )
  }
)

ForwardedButton.displayName = "Button"

export default ForwardedButton
